import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { billingAPI } from '../services/api';
import { Check, Star, Zap, Crown, ArrowLeft, Tag, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const PLAN_FEATURES = {
  weekly: [
    'All premium features',
    'Unlimited mood tracking',
    'Medication management',
    'Just Talk peer support',
    'AI Companion (Amigo)',
    'Wellness Store access',
    'MindPulse™ wellness alerts'
  ],
  monthly: [
    'Everything in Weekly',
    'Better value - save 50%',
    'Priority peer matching',
    'Extended AI conversations'
  ],
  quarterly: [
    'Everything in Monthly',
    'Best value for commitment',
    'Save 17% vs monthly',
    'Exclusive content access'
  ],
  yearly: [
    'Everything in Quarterly',
    'Maximum savings - 30% off',
    'Early access to new features',
    'Priority support',
    'Annual wellness report'
  ]
};

const PLAN_ICONS = {
  weekly: Zap,
  monthly: Star,
  quarterly: Crown,
  yearly: Crown
};

export const PlanAndPricing = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('quarterly');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await billingAPI.getPlans();
      setPlans(response.data.plans || []);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
      toast.error('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      toast.error('Please enter a discount code');
      return;
    }

    if (!isAuthenticated) {
      toast.error('Please sign in to apply discount codes');
      return;
    }

    setValidatingDiscount(true);
    try {
      const response = await billingAPI.validateDiscount(discountCode, selectedPlan);
      setAppliedDiscount(response.data);
      toast.success(`Discount applied! ${response.data.discount_percent}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid discount code');
      setAppliedDiscount(null);
    } finally {
      setValidatingDiscount(false);
    }
  };

  const handleRemoveDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
  };

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      navigate('/auth?mode=register');
      return;
    }

    // Re-validate discount if plan changed
    if (appliedDiscount && planId !== selectedPlan) {
      setAppliedDiscount(null);
      setDiscountCode('');
    }

    setProcessing(planId);
    try {
      const response = await billingAPI.createOrder(planId, appliedDiscount ? discountCode : null);
      const { order_id, amount, currency, key_id, plan_name, discount_applied, original_amount } = response.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'HearMeOut',
        description: `${plan_name} Subscription${discount_applied ? ' (Discounted)' : ''}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            await billingAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planId
            });
            toast.success('Subscription activated! Welcome to Pro!');
            window.location.href = '/dashboard';
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          email: user?.email || '',
          name: user?.full_name || ''
        },
        theme: {
          color: '#8b5cf6'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error('Failed to initiate payment');
    } finally {
      setProcessing(null);
    }
  };

  const getPlanOrder = ['weekly', 'monthly', 'quarterly', 'yearly'];
  const sortedPlans = plans.sort((a, b) => getPlanOrder.indexOf(a.id) - getPlanOrder.indexOf(b.id));

  const getDiscountedPrice = (plan) => {
    if (appliedDiscount && selectedPlan === plan.id) {
      const discounted = plan.price_usd * (1 - appliedDiscount.discount_percent / 100);
      return discounted.toFixed(0);
    }
    return plan.price_usd;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4" data-testid="pricing-page">
      <div className="max-w-7xl mx-auto">
        {/* Back button for authenticated users */}
        {isAuthenticated && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Plans & Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the plan that fits your wellness journey. All plans include full access to premium features.
          </p>
        </div>

        {/* Discount Code Input */}
        <div className="max-w-md mx-auto mb-10">
          <div className="bg-card rounded-xl p-4 border border-border">
            <label className="block text-sm font-medium text-foreground mb-2">
              <Tag className="w-4 h-4 inline mr-2" />
              Have a discount code?
            </label>
            {appliedDiscount ? (
              <div className="flex items-center justify-between bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                <div>
                  <span className="font-bold text-green-500">{appliedDiscount.code}</span>
                  <span className="text-green-400 ml-2">- {appliedDiscount.discount_percent}% off</span>
                </div>
                <button
                  onClick={handleRemoveDiscount}
                  className="p-1 hover:bg-green-500/20 rounded-full"
                >
                  <X className="w-4 h-4 text-green-500" />
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  placeholder="Enter code"
                  className="flex-1 bg-background border border-border rounded-lg px-4 py-2 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="discount-code-input"
                />
                <button
                  onClick={handleApplyDiscount}
                  disabled={validatingDiscount || !discountCode.trim()}
                  className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
                  data-testid="apply-discount-btn"
                >
                  {validatingDiscount ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
            )}
            {!isAuthenticated && (
              <p className="text-xs text-muted-foreground mt-2">Sign in to apply discount codes</p>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sortedPlans.map((plan) => {
            const PlanIcon = PLAN_ICONS[plan.id] || Star;
            const isPopular = plan.id === 'quarterly';
            const features = PLAN_FEATURES[plan.id] || PLAN_FEATURES.weekly;
            const isSelected = selectedPlan === plan.id;
            const hasDiscount = appliedDiscount && isSelected;

            return (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative bg-card rounded-2xl p-6 border cursor-pointer transition-all ${
                  isPopular ? 'border-primary shadow-lg shadow-primary/20' : 'border-border'
                } ${isSelected ? 'ring-2 ring-primary' : ''} flex flex-col`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-white text-sm font-semibold rounded-full">
                    Most Popular
                  </div>
                )}

                <div className="text-center mb-6">
                  <div className={`w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center ${
                    isPopular ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'
                  }`}>
                    <PlanIcon className="w-7 h-7" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                    {plan.name.replace(' Plan', '')}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    {hasDiscount && (
                      <span className="text-xl text-muted-foreground line-through mr-2">${plan.price_usd}</span>
                    )}
                    <span className={`text-4xl font-bold ${hasDiscount ? 'text-green-500' : 'text-foreground'}`}>
                      ${getDiscountedPrice(plan)}
                    </span>
                    <span className="text-muted-foreground">/{plan.id === 'weekly' ? 'week' : plan.id === 'monthly' ? 'month' : plan.id === 'quarterly' ? '3 months' : 'year'}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{plan.amount_display}</p>
                  {hasDiscount && (
                    <span className="inline-block mt-2 px-2 py-1 bg-green-500/20 text-green-500 text-xs font-bold rounded-full">
                      {appliedDiscount.discount_percent}% OFF
                    </span>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {features.slice(0, 5).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={(e) => { e.stopPropagation(); handleSubscribe(plan.id); }}
                  disabled={processing === plan.id}
                  className={`w-full py-3 rounded-full font-semibold transition-all ${
                    isPopular
                      ? 'bg-primary text-white hover:bg-primary/90'
                      : 'bg-muted text-foreground hover:bg-muted/80'
                  } disabled:opacity-50`}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {processing === plan.id ? 'Processing...' : 'Get Started'}
                </button>
              </div>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-foreground mb-4" style={{ fontFamily: 'Nunito, sans-serif' }}>
            All Plans Include
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
            {['Mood Tracking', 'Medication Management', 'Peer Support', 'AI Companion'].map((feature) => (
              <div key={feature} className="flex items-center justify-center gap-2 text-muted-foreground">
                <Check className="w-4 h-4 text-green-500" />
                <span className="text-sm">{feature}</span>
              </div>
            ))}
          </div>
          <p className="text-sm text-muted-foreground mt-6">
            Cancel anytime. No questions asked.
          </p>
        </div>
      </div>
    </div>
  );
};
